/* global.js — site chrome behaviour (vanilla, deferred).
   Drawers (mobile nav + cart), mega-menu keyboard support, sticky header,
   generic disclosures. Exposes window.theme.drawers for cart.js. */
(function () {
  'use strict';
  var theme = (window.theme = window.theme || {});

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  /* ---------------- Drawer manager ---------------- */
  var lastFocus = null;

  function openDrawer(id) {
    var drawer = document.getElementById(id);
    if (!drawer) return;
    // cart.js re-opens the cart drawer after every add, also from inside the
    // open drawer, where the clicked button has just been re-rendered away and
    // focus sits on <body>. Keep the element that first opened it.
    if (drawer.getAttribute('aria-hidden') !== 'false') lastFocus = document.activeElement;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
    // reflect on any toggles
    document.querySelectorAll('[data-drawer-toggle="' + id + '"]').forEach(function (t) {
      t.setAttribute('aria-expanded', 'true');
    });
    var panel = drawer.querySelector('.drawer__panel');
    var first = panel && panel.querySelector(FOCUSABLE);
    if (first) window.requestAnimationFrame(function () { first.focus(); });
    drawer.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
  }

  function closeDrawer(drawer) {
    if (typeof drawer === 'string') drawer = document.getElementById(drawer);
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('.drawer[aria-hidden="false"]')) {
      document.body.classList.remove('drawer-open');
    }
    document.querySelectorAll('[data-drawer-toggle="' + drawer.id + '"]').forEach(function (t) {
      t.setAttribute('aria-expanded', 'false');
    });
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    drawer.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
  }

  function closeAllDrawers() {
    document.querySelectorAll('.drawer[aria-hidden="false"]').forEach(closeDrawer);
  }

  theme.drawers = { open: openDrawer, close: closeDrawer, closeAll: closeAllDrawers };

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-drawer-toggle]');
    if (toggle) {
      e.preventDefault();
      openDrawer(toggle.getAttribute('data-drawer-toggle'));
      return;
    }
    var closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      e.preventDefault();
      closeDrawer(closer.closest('.drawer'));
    }
  });

  /* Focus trap + Escape */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      closeAllDrawers();
      // close any open mega panel by releasing focus
      var openMega = document.querySelector('.mega-menu.is-open');
      if (openMega) {
        openMega.classList.remove('is-open');
        var parentLink = openMega.parentElement.querySelector('[data-mega-toggle]');
        if (parentLink) parentLink.setAttribute('aria-expanded', 'false');
      }
      if (document.activeElement && document.activeElement.closest('.has-mega')) {
        document.activeElement.blur();
      }
      return;
    }
    if (e.key === 'Tab') {
      var drawer = document.querySelector('.drawer[aria-hidden="false"]');
      if (!drawer) return;
      var panel = drawer.querySelector('.drawer__panel');
      var nodes = Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE)).filter(function (n) {
        return n.offsetParent !== null;
      });
      if (!nodes.length) return;
      var firstEl = nodes[0], lastEl = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
      else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
    }
  });

  /* ---------------- Mega-menu (touch / click fallback) ----------------
     Desktop opens on hover/focus via CSS. This adds explicit toggling so
     touch + keyboard users can open without navigating away immediately. */
  // Room between the panel's top (the header's bottom edge, which moves as the
  // announcement bar scrolls away) and the bottom of the window. base.css caps
  // the panel's inner box at this, so a tall menu scrolls instead of running
  // off-screen where the sticky header makes it unreachable.
  var fitMega = function (panel) {
    var room = window.innerHeight - panel.getBoundingClientRect().top - 8;
    panel.style.setProperty('--mega-room', Math.max(240, Math.floor(room)) + 'px');
  };
  document.querySelectorAll('[data-mega-parent]').forEach(function (item) {
    var panel = item.querySelector('[data-mega-panel]');
    if (!panel) return;
    item.addEventListener('pointerenter', function () { fitMega(panel); });
    item.addEventListener('focusin', function () { fitMega(panel); });
  });
  document.querySelectorAll('[data-mega-toggle]').forEach(function (link) {
    link.addEventListener('click', function (e) {
      // Only intercept on coarse pointers (touch); fine pointers use hover.
      if (window.matchMedia('(hover: none)').matches && window.innerWidth >= 961) {
        var panel = link.parentElement.querySelector('[data-mega-panel]');
        if (panel && !panel.classList.contains('is-open')) {
          e.preventDefault();
          document.querySelectorAll('.mega-menu.is-open').forEach(function (p) { p.classList.remove('is-open'); });
          panel.classList.add('is-open');
          link.setAttribute('aria-expanded', 'true');
        }
      }
    });
  });
  document.addEventListener('click', function (e) {
    if (!e.target.closest('.has-mega')) {
      document.querySelectorAll('.mega-menu.is-open').forEach(function (p) {
        p.classList.remove('is-open');
        var l = p.parentElement.querySelector('[data-mega-toggle]');
        if (l) l.setAttribute('aria-expanded', 'false');
      });
    }
  });

  /* ---------------- Sticky header: shadow + auto-hide ----------------
     Adds the drop-shadow once scrolled, and on mobile hides the header when
     scrolling down / reveals it when scrolling up (a "reverse sticky" so the
     menu is out of the way while reading but one flick away). Desktop keeps the
     header pinned — the auto-hide only arms under the mobile media query. */
  var header = document.querySelector('[data-header].header--sticky');
  if (header) {
    var lastY = window.scrollY;
    // Must match the sticky query in sections/header.liquid.
    var mobile = window.matchMedia('(max-width: 960px) and (min-height: 500px)');
    // Don't hide until the user is clearly past the header, so it can't flicker
    // near the top of the page.
    var revealAbove = 140;
    var searchInUse = function () {
      var open = header.querySelector('[data-predictive-results]:not([hidden])');
      return !!open || header.contains(document.activeElement) && document.activeElement.matches('input');
    };

    var onScroll = function () {
      // iOS reports scrollY past the end during the bottom bounce; clamped,
      // the spring back doesn't read as a scroll up and flash the header.
      var maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      var y = Math.min(Math.max(window.scrollY, 0), maxY);
      header.classList.toggle('is-scrolled', y > 8);

      var goingDown = y > lastY;
      // Keep the header visible near the top, while any drawer is open, or on desktop.
      // Also while the shopper is using the header search: opening the phone
      // keyboard, or scrolling the suggestions to their end, scrolls the page,
      // and tucking then slid the box and its suggestions off the screen.
      if (!mobile.matches || document.body.classList.contains('drawer-open') || y < revealAbove || searchInUse()) {
        header.classList.remove('header--tucked');
      } else if (goingDown && y - lastY > 4) {
        header.classList.add('header--tucked');
      } else if (!goingDown && lastY - y > 4) {
        header.classList.remove('header--tucked');
      }
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    // Tabbing onto a header link while it is tucked away brings it back.
    header.addEventListener('focusin', function () { header.classList.remove('header--tucked'); });
    // A resize into desktop must clear any tucked state.
    mobile.addEventListener('change', function () { if (!mobile.matches) header.classList.remove('header--tucked'); });
    onScroll();
  }

  /* ---------------- Generic disclosure (accordions) ----------------
     <button data-toggle aria-controls="ID" aria-expanded="false"> ... <div id="ID"> */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-toggle]');
    if (!trigger) return;
    var id = trigger.getAttribute('aria-controls');
    var panel = id && document.getElementById(id);
    if (!panel) return;
    var expanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(!expanded));
    panel.hidden = expanded;
  });

})();
